import PubSub from 'pubsub-js';
import { Box3, DoubleSide, Mesh, MeshBasicMaterial, Object3D, PlaneGeometry } from 'three';

import { colorToNumber } from '../../../utils/drawing/color';
import { ChangedProjectionNotificationData, CoordinateManager } from '../../coordinate-manager';
import { renderOrders } from '../../render-orders';

export enum ReferencePlaneColors {
  Blue = '#006de0',
  Red = '#db4d69',
  Green = '#00ad83',
  White = '#ffffff',
  Black = '#000000',
  Custom = '#888888',
}

export class ReferencePlane extends Object3D {
  static _instance: ReferencePlane = new ReferencePlane();
  static get instance(): ReferencePlane {
    return this._instance;
  }

  get Visibility() {
    return this.visibility;
  }

  set Visibility(state: boolean) {
    if (this.material) {
      this.material.visible = state;
    }

    this.visibility = state;
  }

  get ReferenceHeight() {
    return CoordinateManager.getInstance().getMAS() + (this.height ? this.height : 0);
  }

  set ReferenceHeight(height: number) {
    this.height = height - CoordinateManager.getInstance().getMAS();
    this.position.setZ(this.height);
  }

  get Opacity() {
    return this.opacity;
  }

  set Opacity(opacity: number) {
    if (this.material) {
      this.material.opacity = opacity;
    }

    this.opacity = opacity;
  }

  get Color() {
    return this.color;
  }

  set Color(color: ReferencePlaneColors) {
    if (this.material) {
      this.material.color.setHex(colorToNumber(color));
    }

    this.color = color;
  }

  /**
   *  Overrides predefined colors using the color hex value.
   */
  set ColorHex(value: string | number) {
    if (this.material) {
      this.material.color.setHex(colorToNumber(value));
    }

    this.color = ReferencePlaneColors.Custom;
  }

  private mesh?: Mesh;
  private material?: MeshBasicMaterial;

  private visibility = false;
  private height?: number;
  private opacity = 1;
  private color: ReferencePlaneColors = ReferencePlaneColors.Blue;

  constructor() {
    super();

    PubSub.subscribe(
      CoordinateManager.getInstance().projectionUpdatedEvent,
      (topic: string, data: ChangedProjectionNotificationData) => {
        if (!data.old.origo || !this.height) {
          return;
        }

        this.height = data.old.origo.z + this.height - data.new.origo.z;
        this.position.setZ(this.height);
      },
    );
  }

  /**
   *  Creates a new reference plane according to the bounding box
   * @param boundingBox
   */
  create(boundingBox: Box3) {
    this.destroy();

    const geometry = new PlaneGeometry(
      boundingBox.max.x - boundingBox.min.x,
      boundingBox.max.y - boundingBox.min.y,
    );
    this.material = new MeshBasicMaterial({
      side: DoubleSide,
      transparent: true,
      opacity: this.opacity,
      visible: this.visibility,
    });
    this.Color = this.color;

    this.mesh = new Mesh(geometry, this.material);
    this.mesh.renderOrder = renderOrders.referencePlane;

    if (!this.height) {
      this.height = boundingBox.min.z + (boundingBox.max.z - boundingBox.min.z) * 0.4;
    }

    this.position.set(
      boundingBox.min.x + geometry.parameters.width / 2,
      boundingBox.min.y + geometry.parameters.height / 2,
      this.height,
    );

    this.add(this.mesh);
  }

  /**
   *  Destroys the current reference plane.
   */
  destroy() {
    if (this.mesh) {
      this.mesh.geometry.dispose();
      this.remove(this.mesh);
      delete this.mesh;
    }

    if (this.material) {
      this.material.dispose();
      delete this.material;
    }

    if (this.parent) {
      this.parent.remove(this);
    }
  }

  /**
   *  Resets all of the values. Should be called when SkyView loads a new project.
   */
  reset() {
    this.destroy();

    this.visibility = false;
    this.height = undefined;
    this.opacity = 1;
    this.color = ReferencePlaneColors.Blue;
  }
}
